import React, { useState, useEffect } from 'react'
import { Container } from '../../../components'
import {
    InspectionDetailReportRequest,
    PropertyGradeResponseData,
    useAnalyticData,
    useDateRange,
    useUser,
} from '../../../hooks'
import {
    Button,
    CircularProgress,
    Modal,
    Paper,
    Slide,
    Theme,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Box,
    TextField,
    ListSubheader,
    InputAdornment,
    IconButton,
    Tooltip,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { axiosInstance } from '../../../helpers/axios'
import {
    ALL,
    CUSTOM,
    DateFilter,
    DateFilterButtons,
    LAST_MONTH,
    THIS_MONTH,
} from '../../../components/DateFilterButtons'
import { getPreviousMonthDates } from '../../../helpers/date'
import { PropertyGradeTable } from './PropertyGradeTable'
import { InspectionTypeGradeTable } from './InspectionTypeGradeTable'
import { GetApp } from '@material-ui/icons'
import { useUserTags } from '../../../contexts/UserTagsContext'
import { typographyStyle } from '../../../theme/typographyStyle'
import { InspectionTypeCategory } from '../../../models/Config/types'

const CATEGORY_LABELS: Record<InspectionTypeCategory, string> = {
    quarterly: 'Quarterly',
    move_out: 'Move Out',
    move_in: 'Move In',
    facilities: 'Facilities',
    other: 'Other',
}
const CATEGORIES = Object.keys(CATEGORY_LABELS) as InspectionTypeCategory[]

interface Props {
    theme: Theme
    ezFolio?: boolean
}

export const PagePropertyGrade = (props: Props) => {
    const { theme, ezFolio } = props
    const [dateFilter, setDateFilter] = useState<DateFilter>(ALL)
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
    const [openInspectionTypeModal, setOpenInspectionTypeModal] = useState(
        false,
    )
    const [
        selectedPropertyGrade,
        setSelectedPropertyGrade,
    ] = useState<PropertyGradeResponseData | null>(null)
    const { dateRange, setStartDate, setEndDate } = useDateRange({
        rangeKey: 'propertyGradeDateRange',
    })
    const {
        loading,
        propertyGradeList,
        chartData,
        setPropertyGradeList,
        getPropertyGradeList,
        setInspectionTypeGrades,
        getChartData,
        getInspectionTypeGrades,
        inspectionTypeGrades,
        setInspectionDetailGrades,
        inspectionDetailGrades,
        getInspectionDetailGrades,
        getPropertyInspectionDetailReport,
        getUnitInspectionCompletionData,
    } = useAnalyticData()

    const { organization } = useUser()
    const { userTags, loading: tagsLoading } = useUserTags()

    const [inspectionTypeNames, setInspectionTypeNames] = useState<string[]>([])
    const [
        selectedInspectionType,
        setSelectedInspectionType,
    ] = useState<string>('')
    const [inspectionTypeSearch, setInspectionTypeSearch] = useState('')
    const [selectedCategory, setSelectedCategory] = useState<
        InspectionTypeCategory | ''
    >('')

    useEffect(() => {
        setSelectedInspectionType('')
        setSelectedCategory('')
    }, [organization])

    useEffect(() => {
        const params: Record<string, number> = {}
        if (organization && ezFolio) {
            params.organization_id = organization
        }
        axiosInstance
            .get('apt_config/inspection_type/get_portfolio_inspection_types/', {
                params,
            })
            .then((res) => setInspectionTypeNames(res.data))
            .catch(() => {})
    }, [organization, ezFolio])

    const buildParams = (extra?: { start_date?: Date; end_date?: Date }) => ({
        ...extra,
        organization: organization && ezFolio ? organization : undefined,
        inspection_type_name: selectedInspectionType || undefined,
        inspection_type_category: selectedCategory || undefined,
    })

    const downloadCsv = () => {
        let data = propertyGradeList ?? []
        if (selectedTagIds.length > 0) {
            data = data.filter((row) => {
                const relevantTags = userTags.filter((tag) =>
                    tag.apartments.includes(row.apartment_id),
                )
                return relevantTags.some((tag) =>
                    selectedTagIds.includes(tag.id),
                )
            })
        }
        if (data.length === 0) return

        const headers = [
            'Location',
            'BIPS',
            'Building Health',
            'Inspector Performance',
            'Units Inspected',
            'Units Requiring Inspection',
            'Inspection %',
            'Last Unit Completed Date',
        ]

        const escapeCell = (val: string) => {
            if (val.includes(',') || val.includes('"') || val.includes('\n')) {
                return `"${val.replace(/"/g, '""')}"`
            }
            return val
        }

        const rows = data.map((row) => {
            const inspectionPct =
                row.units_requiring_inspection > 0
                    ? `${Math.round(
                          (row.units_inspected /
                              row.units_requiring_inspection) *
                              100,
                      )}%`
                    : 'N/A'
            return [
                escapeCell(row.name),
                row.bips,
                row.building_score,
                row.inspector_score,
                row.units_inspected ?? 0,
                row.units_requiring_inspection ?? 0,
                inspectionPct,
                row.last_unit_completed_date
                    ? new Date(
                          row.last_unit_completed_date,
                      ).toLocaleDateString()
                    : '',
            ].join(',')
        })

        const csv = [headers.join(','), ...rows].join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `property_grades_${
            new Date().toISOString().split('T')[0]
        }.csv`
        link.click()
        URL.revokeObjectURL(url)
    }

    useEffect(() => {
        if (dateFilter === ALL) {
            getPropertyGradeList(buildParams())
        } else if (dateFilter === LAST_MONTH) {
            const { startDate, endDate } = getPreviousMonthDates()
            getPropertyGradeList(
                buildParams({ start_date: startDate, end_date: endDate }),
            )
        } else if (dateFilter === THIS_MONTH) {
            const today = new Date()
            const startOfMonth = new Date(
                today.getFullYear(),
                today.getMonth(),
                1,
            )
            const endOfMonth = new Date(
                today.getFullYear(),
                today.getMonth() + 1,
                0,
            )
            getPropertyGradeList(
                buildParams({
                    start_date: startOfMonth,
                    end_date: endOfMonth,
                }),
            )
        }
    }, [dateFilter, organization, selectedInspectionType, selectedCategory])

    const getTimeline = () => {
        if (dateFilter === ALL) {
            return null
        } else if (dateFilter === LAST_MONTH) {
            return getPreviousMonthDates()
        } else if (dateFilter === THIS_MONTH) {
            const today = new Date()
            const startOfMonth = new Date(
                today.getFullYear(),
                today.getMonth(),
                1,
            )
            const endOfMonth = new Date(
                today.getFullYear(),
                today.getMonth() + 1,
                0,
            )
            return { startDate: startOfMonth, endDate: endOfMonth }
        } else if (dateFilter === CUSTOM) {
            return dateRange
        }
        return null
    }

    return (
        <Container direction="column">
            {/* header */}
            <Container
                style={{
                    marginTop: theme.spacing(2),
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <DateFilterButtons
                    dateFilter={dateFilter}
                    setDateFilter={setDateFilter}
                    dateRange={dateRange}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    onApply={() => {
                        getPropertyGradeList(
                            buildParams({
                                start_date: dateRange.startDate,
                                end_date: dateRange.endDate,
                            }),
                        )
                    }}
                />

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(1),
                    }}
                >
                    <FormControl
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 200 }}
                    >
                        <InputLabel>Filter by Tags</InputLabel>
                        <Select
                            multiple
                            value={selectedTagIds}
                            onChange={(e) =>
                                setSelectedTagIds(e.target.value as number[])
                            }
                            label="Filter by Tags"
                            renderValue={(selected) => (
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 4,
                                    }}
                                >
                                    {(selected as number[]).map((tagId) => {
                                        const tag = userTags.find(
                                            (t) => t.id === tagId,
                                        )
                                        return tag ? (
                                            <Chip
                                                key={tagId}
                                                label={tag.name}
                                                size="small"
                                                style={{
                                                    backgroundColor: tag.color,
                                                    color: 'white',
                                                }}
                                            />
                                        ) : null
                                    })}
                                </Box>
                            )}
                            disabled={tagsLoading}
                        >
                            {userTags.map((tag) => (
                                <MenuItem key={tag.id} value={tag.id}>
                                    <Chip
                                        label={tag.name}
                                        size="small"
                                        style={{
                                            backgroundColor: tag.color,
                                            color: 'white',
                                            marginRight: theme.spacing(1),
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 220 }}
                    >
                        <InputLabel>Inspection Type</InputLabel>
                        <Select
                            value={selectedInspectionType}
                            onChange={(e) => {
                                const val = e.target.value as string
                                setSelectedInspectionType(val)
                                if (val) setSelectedCategory('')
                            }}
                            label="Inspection Type"
                            onClose={() => setInspectionTypeSearch('')}
                            MenuProps={{
                                autoFocus: false,
                            }}
                        >
                            <ListSubheader style={{ backgroundColor: 'white' }}>
                                <TextField
                                    size="small"
                                    autoFocus
                                    placeholder="Search..."
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    value={inspectionTypeSearch}
                                    onChange={(e) =>
                                        setInspectionTypeSearch(e.target.value)
                                    }
                                    onKeyDown={(e) => e.stopPropagation()}
                                />
                            </ListSubheader>
                            <MenuItem value="">
                                <em>All Types</em>
                            </MenuItem>
                            {inspectionTypeNames
                                .filter((name) =>
                                    name
                                        .toLowerCase()
                                        .includes(
                                            inspectionTypeSearch.toLowerCase(),
                                        ),
                                )
                                .map((name) => (
                                    <MenuItem key={name} value={name}>
                                        {name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>

                    <FormControl
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 180 }}
                    >
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={selectedCategory}
                            onChange={(e) => {
                                const val = e.target.value as
                                    | InspectionTypeCategory
                                    | ''
                                setSelectedCategory(val)
                                if (val) setSelectedInspectionType('')
                            }}
                            label="Category"
                        >
                            <MenuItem value="">
                                <em>All Categories</em>
                            </MenuItem>
                            {CATEGORIES.map((cat) => (
                                <MenuItem key={cat} value={cat}>
                                    {CATEGORY_LABELS[cat]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Tooltip title="Download CSV">
                        <IconButton onClick={downloadCsv} size="small">
                            <GetApp fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </div>
            </Container>
            {loading ? (
                <Container flex={1} alignItems="center" justifyContent="center">
                    <CircularProgress
                        size={100}
                        style={{ marginTop: theme.spacing(10) }}
                    />
                </Container>
            ) : (
                <PropertyGradeTable
                    propertyGradeList={propertyGradeList}
                    getChartData={getChartData}
                    chartData={chartData}
                    getInspectionTypeGrades={getInspectionTypeGrades}
                    getUnitInspectionCompletionData={
                        getUnitInspectionCompletionData
                    }
                    timeline={dateRange}
                    dateFilter={dateFilter}
                    setPropertyGradeList={setPropertyGradeList}
                    setOpenInspectionTypeModal={setOpenInspectionTypeModal}
                    setSelectedPropertyGrade={setSelectedPropertyGrade}
                    selectedPropertyGrade={selectedPropertyGrade}
                    selectedTagIds={selectedTagIds}
                    inspectionTypeFilters={{
                        inspection_type_name:
                            selectedInspectionType || undefined,
                        inspection_type_category: selectedCategory || undefined,
                    }}
                />
            )}

            <Modal
                open={openInspectionTypeModal}
                onClose={() => {
                    setOpenInspectionTypeModal(false)
                    setSelectedPropertyGrade(null)
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={openInspectionTypeModal}>
                    <Paper
                        style={{
                            padding: theme.spacing(2),
                            minWidth: '900px',
                        }}
                    >
                        <Container>
                            <Container
                                style={{
                                    ...typographyStyle(theme.typography.h4),
                                    flex: 1,
                                }}
                            >
                                {selectedPropertyGrade?.name}
                            </Container>
                            <Container style={{ alignSelf: 'flex-end' }}>
                                <Button
                                    onClick={() => {
                                        if (selectedPropertyGrade?.id) {
                                            const dateRange = getTimeline()
                                            const request: InspectionDetailReportRequest = {
                                                params: {
                                                    apartment_id:
                                                        selectedPropertyGrade.id,
                                                },
                                            }
                                            if (dateRange) {
                                                request.params.end_date =
                                                    dateRange.endDate
                                                request.params.start_date =
                                                    dateRange.startDate
                                            }
                                            getPropertyInspectionDetailReport(
                                                request,
                                            )
                                        }
                                    }}
                                    style={{
                                        backgroundColor:
                                            theme.palette.primary.dark,
                                        color: 'white',
                                        textTransform: 'none',
                                        cursor: 'pointer',
                                        padding: theme.spacing(1),
                                        margin: theme.spacing(1, 2, 1, 0),
                                    }}
                                >
                                    Full Report <GetApp fontSize="small" />
                                </Button>
                            </Container>
                        </Container>
                        <Container
                            style={{ ...typographyStyle(theme.typography.h6) }}
                        >
                            BIPS: {selectedPropertyGrade?.bips}
                        </Container>
                        <Container>
                            <InspectionTypeGradeTable
                                getInspectionDetailGrades={
                                    getInspectionDetailGrades
                                }
                                timeline={dateRange}
                                dateFilter={dateFilter}
                                inspectionTypeGrades={inspectionTypeGrades}
                                setInspectionTypeGrades={
                                    setInspectionTypeGrades
                                }
                                inspectionDetailGrades={inspectionDetailGrades}
                                setInspectionDetailGrades={
                                    setInspectionDetailGrades
                                }
                                getPropertyInspectionDetailReport={
                                    getPropertyInspectionDetailReport
                                }
                                getTimeline={getTimeline}
                            />
                        </Container>
                    </Paper>
                </Slide>
            </Modal>
        </Container>
    )
}
